import { Anchor, Group } from '@mantine/core';
import { dialablePhone, formatPhone } from '../../lib/phone';

/**
 * The people at a vendor, shared by the vendor's own screen and by the list of all vendors.
 *
 * Both screens have to answer the same question — "who do I call, and on what?" — and they used to
 * answer it from a single phone column on the supplier row. A vendor like Madeyso has an owner plus
 * people at two of its own counters, so the answer is a small collection, and the rules for reading
 * that collection (who comes first, which number to try) belong in one place rather than in each
 * screen's rendering code.
 */

export interface SupplierContact {
  id: string;
  supplierId: string;
  supplierBranchId?: string | null;
  /** Joined by the API — the name of the vendor's place, or null when they answer for all of it. */
  branchName?: string | null;
  name: string;
  role?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  isPrimary: boolean;
  notes?: string | null;
  active: boolean;
}

export interface SupplierBranch {
  id: string;
  supplierId: string;
  name: string;
  address?: string | null;
  phone?: string | null;
  notes?: string | null;
  active: boolean;
}

/** Every vendor's people in one request — the supplier list needs a name against each row. */
export const ALL_CONTACTS_PATH = '/admin/v1/suppliers/contacts';
export const contactsPath = (supplierId: string) => `/admin/v1/suppliers/suppliers/${supplierId}/contacts`;
export const branchesPath = (supplierId: string) => `/admin/v1/suppliers/suppliers/${supplierId}/branches`;

export interface Reach {
  href: string;
  label: string;
  /** Shown after the number, so a tap is never a guess about which app opens. */
  via?: string;
  external?: boolean;
}

/**
 * Every way to reach one person, in the order somebody here would actually try them: WhatsApp is
 * how orders go out on this island, a phone call is next, and email is what you use when neither
 * answers. Returned as a list rather than one best answer, because the vendor's own screen shows
 * all of them and a table cell shows the first.
 */
export function waysToReach(who: {
  whatsapp?: string | null;
  phone?: string | null;
  email?: string | null;
}): Reach[] {
  const trim = (v: string | null | undefined) => (typeof v === 'string' ? v.trim() : '');
  const whatsapp = trim(who.whatsapp);
  const phone = trim(who.phone);
  const email = trim(who.email);
  return [
    // wa.me takes digits only; the stored number keeps whatever spacing the owner typed.
    ...(whatsapp
      ? [
          {
            href: `https://wa.me/${whatsapp.replace(/[^0-9]/g, '')}`,
            label: formatPhone(whatsapp),
            via: 'WhatsApp',
            external: true,
          },
        ]
      : []),
    ...(phone ? [{ href: `tel:${dialablePhone(phone)}`, label: formatPhone(phone), via: 'Call' }] : []),
    ...(email ? [{ href: `mailto:${email}`, label: email }] : []),
  ];
}

/** One tappable way to reach someone. */
export function ReachLink({ way, size = 'sm' }: { way: Reach; size?: 'xs' | 'sm' }) {
  return (
    <Anchor href={way.href} target={way.external ? '_blank' : undefined} size={size}>
      {way.label}
      {way.via && (
        <span style={{ color: 'var(--mantine-color-dimmed)', fontSize: 'var(--mantine-font-size-xs)' }}>
          {' · '}
          {way.via}
        </span>
      )}
    </Anchor>
  );
}

/** Every way to reach one person, side by side. */
export function ReachLinks({ ways, size = 'sm' }: { ways: Reach[]; size?: 'xs' | 'sm' }) {
  return (
    <Group gap="md" wrap="wrap">
      {ways.map((way) => (
        <ReachLink key={way.href} way={way} size={size} />
      ))}
    </Group>
  );
}

/**
 * Who to ring. The vendor's chosen contact when there is one, and otherwise simply the first
 * person on file — a vendor with one unflagged contact is reachable, and reading it as "no number"
 * would be a screen arguing with what is plainly recorded on it.
 */
export function whoToCall(contacts: SupplierContact[]): SupplierContact | undefined {
  const here = contacts.filter((c) => c.active);
  return here.find((c) => c.isPrimary) ?? here[0];
}
